// src/Pages/BlitzPay/BlitzSplit/BlitzSplitManageDialog.jsx — v10.3
//
// CHANGES vs v10.2
// ────────────────
// 1. NET-30 CONFIRM DIALOG — clicking "Net-{term}" on a PayoutRow now opens
//    a confirmation dialog before any API call. Dialog shows:
//      • contractor name + amount
//      • what net-{term} means (full gross, no fee, auto-settles at funding)
//      • "they can still elect instant themselves before funding"
//      • expected settlement date if invoice is approved
//    Confirm → POST /bbc/payments/<id>/decision { decision: 'wait' }
//    Cancel  → nothing recorded, row unchanged
//
// 2. LOCAL STATE AFTER WAIT — on success the payments array is updated
//    locally: payment.decision = 'wait'. This causes PayoutRow to re-render
//    with the locked "Waiting on transfer" status chip and no action buttons,
//    without a full re-fetch.
//
// 3. canElect GUARD — updated so rows with decision='wait' already set
//    no longer show the two-button action bar (the SME can't change their mind;
//    the creator can override from their own portal).
//
// 4. vendorStatusMeta 'wait' path — already correct. No change needed there.
//
// 5. BACKEND NOTE — bbc_routes.py decision route must allow SME to POST
//    decision='wait'. See bbc_routes.py v9.1 diff in this PR.

import React, {
  useState, useEffect, useCallback, useRef, useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Avatar, Button, Chip, IconButton,
  CircularProgress, Alert, LinearProgress, TextField,
  InputAdornment, Collapse, Tooltip, Paper, Stack, Dialog,
  DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import {
  Bolt as BoltIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  HourglassEmpty as PendingIcon,
  Payment as PaymentIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Email as EmailIcon,
  InfoOutlined as InfoIcon,
  Warning as WarningIcon,
  Upload as UploadIcon,
  TableChart as CSVIcon,
  AccountBalanceWallet as WithdrawIcon,
  LockOutlined as LockIcon,
  Save as SaveIcon,
  Splitscreen as SplitIcon,
  CheckCircleOutlined as PaidIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import { differenceInDays, addDays, parseISO, format, isValid } from "date-fns";
import client from "../../../API";

// ── Brand tokens ───────────────────────────────────────────────── //
const Y       = "#FFD600";
const Y_DARK  = "#ECC900";
const Y_INK   = "#1A1200";
const Y_BROWN = "#7a5c00";
const Y_TINT  = "#fffbeb";
const Y_BDR   = "rgba(186,117,23,0.20)";

const INSTANT_FEE = 0.05;

const PAL = [
  { bg: "#e8f4e8", text: "#2d5a2d", dot: "#7cb97c" },
  { bg: "#e8eef8", text: "#1a3a6b", dot: "#7c9bd4" },
  { bg: "#f4e8f8", text: "#4a1a6b", dot: "#b07cd4" },
  { bg: "#f8ece8", text: "#6b2a1a", dot: "#d4907c" },
  { bg: "#e8f8f4", text: "#1a5a4a", dot: "#7cd4b8" },
  { bg: "#f8f4e8", text: "#5a4a1a", dot: "#d4c07c" },
  { bg: "#f0e8f8", text: "#3a1a6b", dot: "#9a7cd4" },
];

// ── Helpers ─────────────────────────────────────────────────────── //
const fmt = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD", maximumFractionDigits: 0,
  }).format(n ?? 0);

const fmtCents = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD",
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(n ?? 0);

const fmtDate = (d) => {
  if (!d) return null;
  const p = typeof d === "string" ? parseISO(d) : d;
  return isValid(p) ? format(p, "MMM d") : null;
};

const termLabel = (days) => {
  if (days == null) return "Standard";
  if (days === 0)   return "Due on receipt";
  return `Net ${days}`;
};

const isEmailish = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((s || "").trim());

const parseCSV = (text) => {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return [];
  const headers = lines[0].toLowerCase().split(",").map((h) => h.trim().replace(/"/g, ""));
  const emailIdx  = headers.indexOf("email");
  const amountIdx = headers.indexOf("amount");
  if (emailIdx === -1) return [];
  return lines.slice(1).reduce((acc, line) => {
    const cols  = line.split(",").map((c) => c.trim().replace(/"/g, ""));
    const email = cols[emailIdx] || "";
    if (!isEmailish(email)) return acc;
    const amount = amountIdx >= 0 ? parseFloat(cols[amountIdx]) || 0 : 0;
    acc.push({ email, amount });
    return acc;
  }, []);
};

const INV_STATUS_CFG = {
  pending:  { label: "Awaiting approval",           color: "warning", icon: <PendingIcon /> },
  approved: { label: "Approved — instant unlocked", color: "success", icon: <CheckCircleIcon /> },
  paid:     { label: "Funded",                      color: "info",    icon: <PaymentIcon /> },
  funded:   { label: "Funded",                      color: "info",    icon: <PaymentIcon /> },
};

const vendorStatusMeta = (rawStatus, isPre, invoiceStatus, decision) => {
  const s = (rawStatus || "promised").toLowerCase();
  if (s === "instant_claimed") return {
    label:   "⚡ Instant paid",
    chipSx:  { bgcolor: Y_TINT, color: Y_BROWN, border: `1px solid ${Y_BDR}` },
    tooltip: "Already paid out via BlitzPay. The BlitzPay fee was retained from the invoice advance.",
    isPaid:  true,
  };
  if (s === "settled") return {
    label:   "✓ Settled",
    chipSx:  { bgcolor: "#e8f5e9", color: "#2e7d32", border: "1px solid #a5d6a7" },
    tooltip: "Paid in full at net term — no BlitzPay fee.",
    isPaid:  true,
  };
  // decision='wait' set by SME — row is locked but creator can still override
  if (s === "promised" && decision === "wait") return {
    label:   "Net term — locked",
    chipSx:  { bgcolor: "#f0f4ff", color: "#1565c0", border: "1px solid #90caf9" },
    tooltip: "You've set this to settle at net term. The contractor can still elect instant from their own portal before funding.",
    isPaid:  false,
  };
  if (s === "wait") return {
    label:   "Waiting on transfer",
    chipSx:  { bgcolor: "#f5f5f5", color: "#616161", border: "1px solid #e0e0e0" },
    tooltip: "Invoice approved — waiting on enterprise transfer. Pays out automatically once cleared.",
    isPaid:  false,
  };
  if (isPre) return {
    label:   "⚡ Pre-accepted",
    chipSx:  { bgcolor: Y_TINT, color: Y_BROWN, border: `1px solid ${Y_BDR}` },
    tooltip: "Converts to instant automatically at approval. Fee retained from the invoice advance.",
    isPaid:  false,
  };
  if (invoiceStatus === "pending") return {
    label:   "Pending approval",
    chipSx:  { bgcolor: "#fff8e1", color: "#f57f17", border: "1px solid #ffe082" },
    tooltip: "Waiting for enterprise approval.",
    isPaid:  false,
  };
  return {
    label:   "Awaiting election",
    chipSx:  { bgcolor: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db" },
    tooltip: "Invoice approved — hasn't chosen yet. Elect instant on their behalf or let them wait for full payout.",
    isPaid:  false,
  };
};

// ─────────────────────────────────────────────────────────────────
// REMOVE CONFIRM DIALOG
// ─────────────────────────────────────────────────────────────────
const RemoveConfirmDialog = ({ seg, onConfirm, onCancel }) => (
  <Dialog open={!!seg} onClose={onCancel} maxWidth="xs" fullWidth
    PaperProps={{ sx: { borderRadius: 2.5, p: 0.5 } }}>
    <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem", pb: 0.5 }}>
      Remove from split?
    </DialogTitle>
    <DialogContent>
      <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.6 }}>
        <strong>{seg?.name}</strong>
        {seg?.email ? ` (${seg.email})` : ""} will be removed from this invoice.
        {seg?.amount > 0 && (
          <> Their {fmtCents(seg.amount)} slice will be freed back to your margin.</>
        )}
        {" "}This cannot be undone while the invoice is approved.
      </Typography>
    </DialogContent>
    <DialogActions sx={{ px: 2, pb: 1.5, gap: 1 }}>
      <Button onClick={onCancel} variant="outlined" size="small"
        sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600 }}>Cancel</Button>
      <Button onClick={onConfirm} variant="contained" size="small" color="error"
        sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 700 }}>Yes, remove</Button>
    </DialogActions>
  </Dialog>
);

// ─────────────────────────────────────────────────────────────────
// NET-TERM CONFIRM DIALOG  ← NEW in v10.3
//
// Shown when the SME clicks "Net-{term}" on a contractor row.
// This records a binding intent: the contractor will settle at full
// gross, no fee. They can still override to instant from their own
// portal before enterprise funding arrives.
// ─────────────────────────────────────────────────────────────────
const NetTermConfirmDialog = ({ seg, paymentTerm, settleDate, onConfirm, onCancel, saving }) => {
  if (!seg) return null;
  const gross   = seg.amount || 0;
  const termStr = paymentTerm ?? 30;
  const settleDateStr = settleDate ? format(settleDate, "MMM d, yyyy") : null;

  return (
    <Dialog open={!!seg} onClose={onCancel} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 2.5, p: 0.5 } }}>
      <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem", pb: 0.5,
        display: "flex", alignItems: "center", gap: 0.75 }}>
        <ScheduleIcon sx={{ fontSize: 18, color: "#1565c0" }} />
        Set to net-{termStr}?
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.65, mb: 1.5 }}>
          <strong>{seg.name}</strong> will be set to receive{" "}
          <strong>{fmtCents(gross)}</strong> in full at net-{termStr} — no fee deducted.
          Settlement is automatic once the enterprise funds the invoice.
        </Typography>

        {/* What this means */}
        <Box sx={{ borderRadius: 1.5, border: "0.5px solid #90caf9",
          bgcolor: "#f0f4ff", overflow: "hidden", mb: 1.5 }}>
          {[
            { label: "They receive",             value: fmtCents(gross),          bold: true },
            { label: "BlitzPay fee",              value: "None — $0.00",           note: true },
            { label: "Settlement",               value: settleDateStr ? `~${settleDateStr}` : `Net-${termStr}` },
            { label: "Your balance",             value: "Unchanged",               note: true },
          ].map(({ label, value, bold, note }) => (
            <Box key={label} sx={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              px: 1.5, py: 0.75,
              borderBottom: "0.5px solid #bbdefb",
              "&:last-child": { borderBottom: "none" },
            }}>
              <Typography variant="caption" sx={{
                color: note ? "text.disabled" : "text.secondary",
                fontSize: "0.72rem" }}>
                {label}
              </Typography>
              <Typography sx={{
                fontWeight: bold ? 800 : 600, fontSize: "0.85rem",
                fontVariantNumeric: "tabular-nums",
                color: bold ? "#1565c0" : note ? "text.disabled" : "text.primary" }}>
                {value}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Creator override note */}
        <Box sx={{ px: 1.25, py: 0.85, borderRadius: 1.5,
          bgcolor: alpha("#f59e0b", 0.07), border: "0.5px solid", borderColor: alpha("#f59e0b", 0.35) }}>
          <Typography variant="caption" sx={{ color: "#92400e", fontSize: "0.65rem", lineHeight: 1.5 }}>
            <strong>Note:</strong> {seg.name} can still log in to their Blitz portal and elect instant
            ({fmtCents(gross * (1 - INSTANT_FEE))} after the {Math.round(INSTANT_FEE * 100)}% fee)
            at any time before enterprise funding arrives. Once you confirm here,
            <strong> you cannot change this back</strong> — only they can override it.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 1.5, gap: 1 }}>
        <Button onClick={onCancel} variant="outlined" size="small"
          sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600 }}>
          Cancel
        </Button>
        <Button onClick={onConfirm} variant="contained" size="small"
          disabled={saving}
          startIcon={saving
            ? <CircularProgress size={10} sx={{ color: "white" }} />
            : <ScheduleIcon sx={{ fontSize: "12px !important" }} />}
          sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 700,
            bgcolor: "#1565c0", color: "white",
            "&:hover": { bgcolor: "#0d47a1" }, boxShadow: "none" }}>
          {saving ? "Saving…" : `Confirm net-${termStr}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────
// BULK CONVERT CONFIRM DIALOG
// ─────────────────────────────────────────────────────────────────
const ConvertConfirmDialog = ({ open, promisedRows, feeRate, onConfirm, onCancel }) => {
  const totalGross = promisedRows.reduce((s, p) => s + parseFloat(p.amount || 0), 0);
  const feeRatePct = parseFloat(feeRate ?? 5);
  const totalFee   = totalGross * feeRatePct / 100;
  const totalNet   = totalGross - totalFee;
  const count      = promisedRows.length;

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 2.5, p: 0.5 } }}>
      <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem", pb: 0.5,
        display: "flex", alignItems: "center", gap: 0.75 }}>
        <BoltIcon sx={{ color: Y_BROWN, fontSize: 18 }} />
        Convert {count} contractor{count !== 1 ? "s" : ""} to instant?
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.65, mb: 1.5 }}>
          All {count} remaining contractor{count !== 1 ? "s" : ""} will receive their payout
          immediately. The {feeRatePct}% BlitzPay fee is retained from the invoice advance —
          it does <strong>not</strong> come out of your Blitz balance.
        </Typography>
        <Box sx={{ borderRadius: 1.5, border: `0.5px solid ${Y_BDR}`, bgcolor: Y_TINT, overflow: "hidden" }}>
          {[
            { label: `${count} contractor${count !== 1 ? "s" : ""} · gross total`, value: fmtCents(totalGross) },
            { label: `BlitzPay fee (${feeRatePct}%) — from invoice advance`,        value: `−${fmtCents(totalFee)}`, warn: true },
            { label: "They receive",                                                  value: fmtCents(totalNet),   bold: true },
            { label: "Your balance: unchanged",                                       value: "—",                   note: true },
          ].map(({ label, value, warn, bold, note }) => (
            <Box key={label} sx={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              px: 1.5, py: 0.75,
              borderBottom: "0.5px solid", borderColor: Y_BDR,
              "&:last-child": { borderBottom: "none" },
            }}>
              <Typography variant="caption" sx={{
                color: warn ? "#c62828" : note ? "text.disabled" : "text.secondary",
                fontSize: "0.72rem", fontWeight: bold ? 700 : 400 }}>
                {label}
              </Typography>
              <Typography sx={{
                fontWeight: bold ? 800 : 600, fontSize: "0.85rem",
                fontVariantNumeric: "tabular-nums",
                color: warn ? "#c62828" : note ? "text.disabled" : "text.primary" }}>
                {value}
              </Typography>
            </Box>
          ))}
        </Box>
        <Typography variant="caption" sx={{ display: "block", mt: 1.25,
          color: "text.disabled", fontSize: "0.65rem", lineHeight: 1.5 }}>
          Recipients without a Blitz account are pre-accepted and credited automatically when they sign up.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 1.5, gap: 1 }}>
        <Button onClick={onCancel} variant="outlined" size="small"
          sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600 }}>Cancel</Button>
        <Button onClick={onConfirm} variant="contained" size="small"
          startIcon={<BoltIcon sx={{ fontSize: "12px !important" }} />}
          sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 700,
            bgcolor: Y, color: Y_INK, "&:hover": { bgcolor: Y_DARK }, boxShadow: "none" }}>
          Confirm — convert {count}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────
// BUDGET TRACKER BAR
// ─────────────────────────────────────────────────────────────────
const BudgetTracker = ({ total, instantPaidSum, pendingSum, margin, unallocated }) => {
  if (!total) return null;
  const pct = (v) => `${Math.max(0, (v / total) * 100).toFixed(1)}%`;
  const unallocatedActual = Math.max(0, total - instantPaidSum - pendingSum - margin);

  return (
    <Box>
      <Box sx={{ height: 10, borderRadius: 999, display: "flex", overflow: "hidden",
        border: "0.5px solid", borderColor: "divider", mb: 1 }}>
        {instantPaidSum > 0 && (
          <Tooltip title={`${fmtCents(instantPaidSum)} paid instantly`}>
            <Box sx={{ width: pct(instantPaidSum), bgcolor: Y, transition: "width 0.3s" }} />
          </Tooltip>
        )}
        {pendingSum > 0 && (
          <Tooltip title={`${fmtCents(pendingSum)} pending payout`}>
            <Box sx={{ width: pct(pendingSum), bgcolor: "#1976d2", opacity: 0.7, transition: "width 0.3s" }} />
          </Tooltip>
        )}
        {margin > 0 && (
          <Tooltip title={`${fmtCents(margin)} your margin`}>
            <Box sx={{ width: pct(margin), bgcolor: "#2e7d32", opacity: 0.6, transition: "width 0.3s" }} />
          </Tooltip>
        )}
        {unallocatedActual > 0 && (
          <Tooltip title={`${fmtCents(unallocatedActual)} unallocated`}>
            <Box sx={{ flex: 1, bgcolor: "action.selected" }} />
          </Tooltip>
        )}
      </Box>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        {[
          { color: Y,          label: "Paid instantly",  value: instantPaidSum, show: instantPaidSum > 0 },
          { color: "#1976d2",  label: "Pending payout",  value: pendingSum,     show: pendingSum > 0 },
          { color: "#2e7d32",  label: "Your margin",     value: margin,         show: margin > 0 },
          { color: "action.selected", label: "Unallocated", value: unallocatedActual, show: unallocatedActual > 0.5 },
        ].filter((r) => r.show).map(({ color, label, value }) => (
          <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.62rem" }}>
              {label}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: "0.62rem",
              fontVariantNumeric: "tabular-nums" }}>
              {fmtCents(value)}
            </Typography>
          </Box>
        ))}
      </Box>
      {unallocatedActual > 0.5 && (
        <Box sx={{ mt: 0.75, px: 1, py: 0.6, borderRadius: 1.5,
          bgcolor: alpha("#f59e0b", 0.08), border: "0.5px solid", borderColor: alpha("#f59e0b", 0.3) }}>
          <Typography variant="caption" sx={{ color: "#92400e", fontSize: "0.65rem", fontWeight: 600 }}>
            {fmtCents(unallocatedActual)} unallocated — add contractors or it becomes your margin
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────
// NET-30 DIAL
// ─────────────────────────────────────────────────────────────────
const DosageDial = ({ inv, bbcSummary, payments }) => {
  const paymentTerm  = bbcSummary?.payment_term ?? inv?.payment_term ?? 30;
  const approvedRaw  = inv?.approved_at || bbcSummary?.approved_at || inv?.date_approved;
  const approvedDate = approvedRaw ? parseISO(approvedRaw) : null;
  const settleDate   = approvedDate && isValid(approvedDate)
    ? addDays(approvedDate, parseInt(paymentTerm) || 30) : null;

  const today     = new Date();
  const daysLeft  = settleDate ? Math.max(0, differenceInDays(settleDate, today)) : null;
  const elapsed   = approvedDate && isValid(approvedDate)
    ? Math.max(0, differenceInDays(today, approvedDate)) : 0;
  const totalDays = parseInt(paymentTerm) || 30;
  const pct       = Math.min(1, totalDays > 0 ? elapsed / totalDays : 0);

  const waitTotal = payments
    .filter((p) => ["wait", "promised"].includes((p.status || "promised").toLowerCase())
      && !(p.preapproved || p.blitzpay_preapproved))
    .reduce((s, p) => s + parseFloat(p.amount || 0), 0);

  const R = 38; const CX = 48; const CY = 48;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const arcAngle = pct * 270; const startAngle = 135;
  const endAngle  = startAngle + arcAngle;
  const arcX1 = CX + R * Math.cos(toRad(startAngle));
  const arcY1 = CY + R * Math.sin(toRad(startAngle));
  const arcX2 = CX + R * Math.cos(toRad(endAngle));
  const arcY2 = CY + R * Math.sin(toRad(endAngle));
  const largeArc = arcAngle > 180 ? 1 : 0;
  const trackX1 = CX + R * Math.cos(toRad(135));
  const trackY1 = CY + R * Math.sin(toRad(135));
  const trackX2 = CX + R * Math.cos(toRad(405));
  const trackY2 = CY + R * Math.sin(toRad(405));
  const arcColor = pct >= 0.9 ? "#4caf50" : Y;

  if (!approvedDate || !isValid(approvedDate)) {
    return (
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: "50%", bgcolor: alpha(Y, 0.12),
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Typography sx={{ fontSize: "0.6rem", color: Y_BROWN, fontWeight: 700 }}>N/A</Typography>
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: "0.82rem", lineHeight: 1.2 }}>Net-{paymentTerm} window</Typography>
            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.68rem" }}>
              Countdown starts at enterprise approval
            </Typography>
          </Box>
        </Box>
        <Box sx={{ height: 5, borderRadius: 999, bgcolor: alpha("#1976d2", 0.12) }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5 }}>
        <Box sx={{ flexShrink: 0 }}>
          <svg width="96" height="96" viewBox="0 0 96 96">
            <path d={`M ${trackX1} ${trackY1} A ${R} ${R} 0 1 1 ${trackX2} ${trackY2}`}
              fill="none" stroke="currentColor" strokeOpacity="0.1" strokeWidth="6" strokeLinecap="round" />
            {pct > 0 && (
              <path d={`M ${arcX1} ${arcY1} A ${R} ${R} 0 ${largeArc} 1 ${arcX2} ${arcY2}`}
                fill="none" stroke={arcColor} strokeWidth="6" strokeLinecap="round" />
            )}
            <text x={CX} y={CY - 4} textAnchor="middle" fontSize="22" fontWeight="600"
              fill={Y_BROWN} fontFamily="inherit">{daysLeft ?? "—"}</text>
            <text x={CX} y={CY + 13} textAnchor="middle" fontSize="8"
              fill="currentColor" fillOpacity="0.4" fontFamily="inherit">days left</text>
          </svg>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "0.88rem", lineHeight: 1.3, mb: 0.25 }}>
            {waitTotal > 0 ? fmt(waitTotal) : "—"}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.68rem", display: "block" }}>
            settles fee-free at net-{paymentTerm}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.66rem", display: "block", mt: 0.5 }}>
            {[fmtDate(approvedDate) ? `Approved ${fmtDate(approvedDate)}` : null,
              fmtDate(settleDate)   ? `· Settles ${fmtDate(settleDate)}`  : null,
            ].filter(Boolean).join(" ")}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ height: 5, borderRadius: 999, bgcolor: alpha("#1976d2", 0.12), overflow: "hidden" }}>
        <Box sx={{ width: `${Math.max(pct * 100, 2)}%`, height: "100%",
          bgcolor: pct >= 0.9 ? "#4caf50" : "#1976d2", borderRadius: 999,
          transition: "width 0.4s ease" }} />
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
        <Typography variant="caption" sx={{ color: "text.disabled", fontSize: "0.6rem" }}>{elapsed}d elapsed</Typography>
        <Typography variant="caption" sx={{ color: "text.disabled", fontSize: "0.6rem" }}>{Math.round(pct * 100)}% · Net-{totalDays}</Typography>
      </Box>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────
// AMOUNT EDITOR
// ─────────────────────────────────────────────────────────────────
const AmountEditor = ({ amount, onCommit, locked, paid, accentColor, warn }) => {
  const [val, setVal] = useState(String(amount ?? 0));
  const prevAmount    = useRef(amount);

  useEffect(() => {
    if (amount !== prevAmount.current) {
      setVal(String(amount ?? 0));
      prevAmount.current = amount;
    }
  }, [amount]);

  const commit = () => {
    const parsed = parseFloat(val.replace(/[^0-9.]/g, ""));
    if (!isNaN(parsed) && parsed >= 0) onCommit(parsed);
    else setVal(String(amount ?? 0));
  };

  if (paid) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
        <PaidIcon sx={{ fontSize: 13, color: "success.main" }} />
        <Typography sx={{ fontWeight: 700, fontSize: "0.95rem",
          fontVariantNumeric: "tabular-nums", color: "success.main", minWidth: 60, textAlign: "right" }}>
          {fmtCents(amount)}
        </Typography>
      </Box>
    );
  }

  if (locked) {
    return (
      <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", fontVariantNumeric: "tabular-nums",
        color: accentColor || "text.primary", minWidth: 64, textAlign: "right", flexShrink: 0 }}>
        {fmt(amount)}
      </Typography>
    );
  }

  return (
    <TextField size="small" value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={commit}
      onFocus={(e) => e.target.select()}
      onKeyDown={(e) => {
        if (e.key === "Enter") { commit(); e.target.blur(); }
        if (e.key === "Escape") { setVal(String(amount ?? 0)); e.target.blur(); }
        e.stopPropagation();
      }}
      placeholder="0"
      InputProps={{
        startAdornment: <InputAdornment position="start">
          <Typography sx={{ fontSize: "0.75rem", color: "text.disabled" }}>$</Typography>
        </InputAdornment>,
        sx: {
          height: 34, fontSize: "0.9rem", fontWeight: 700,
          fontVariantNumeric: "tabular-nums", borderRadius: 1.5,
          bgcolor: warn ? alpha("#f44336", 0.06) : "background.paper",
          "& input": { textAlign: "right", pr: 0.75, color: warn ? "#c62828" : (accentColor || "inherit") },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: warn ? "#f44336" : undefined,
            borderStyle: warn ? "dashed" : "solid",
          },
        },
      }}
      sx={{ width: 110, flexShrink: 0 }}
      onClick={(e) => e.stopPropagation()}
    />
  );
};

// ─────────────────────────────────────────────────────────────────
// PAYOUT ROW — v10.3
// canElect excludes rows where decision='wait' is already recorded
// Once net-term is set, the row shows a locked blue chip instead of buttons
// ─────────────────────────────────────────────────────────────────
const PayoutRow = ({
  seg, total, palIdx, invoiceStatus, invoicePaymentTerm, feeRate, onElect, electing,
  onRemove, onAmountChange, onWithdrawMargin, withdrawingMargin, ledgerClear,
}) => {
  const isYou  = seg.kind === "you";
  const pal    = isYou ? null : PAL[palIdx % PAL.length];
  const pct    = total > 0 ? (seg.amount / total) * 100 : 0;
  const status = (seg.status || "promised").toLowerCase();
  const isPre  = seg.preapproved;
  const dec    = seg.decision;  // 'wait' | 'instant' | null

  const isDone  = ["instant_claimed", "settled"].includes(status) && seg.amount > 0;
  const isZero  = !isYou && seg.amount === 0;
  // Net-term locked = decision='wait' set (by SME or creator) but not yet settled
  const isNetLocked = !isYou && dec === "wait" && status === "promised";

  const canEditAmount = !isYou && !isDone && !isNetLocked;
  // canElect: must be approved, promised, not pre-accepted, not zero, not yourself,
  // AND decision must not already be 'wait'
  const canElect = !isYou && !isZero && invoiceStatus === "approved"
    && status === "promised" && !isPre && dec !== "wait";
  const canRemove = !isYou && !isDone;

  const isFunded       = ["paid", "funded"].includes(invoiceStatus);
  const canWithdraw    = isYou && isFunded && seg.amount > 0 && ledgerClear;
  const withdrawBlocked = isYou && isFunded && seg.amount > 0 && !ledgerClear;

  const feeAmt         = seg.amount * INSTANT_FEE;
  const netAmt         = seg.amount - feeAmt;
  const showFeePreview = !isYou && (canElect || isPre) && !isDone && seg.amount > 0;
  const statusMeta     = isYou ? null : vendorStatusMeta(seg.status, isPre, invoiceStatus, dec);

  return (
    <Box sx={{
      display: "flex", alignItems: "center", gap: 1,
      px: 1.25, py: 1, borderRadius: 1.5, border: "0.5px solid",
      borderColor: isDone
        ? (status === "instant_claimed" ? alpha(Y, 0.5) : "#a5d6a7")
        : isNetLocked ? "#90caf9"
        : isZero ? "#f44336"
        : isYou ? Y_BDR : "divider",
      bgcolor: isDone
        ? (status === "instant_claimed" ? alpha(Y, 0.04) : alpha("#2e7d32", 0.04))
        : isNetLocked ? alpha("#1565c0", 0.04)
        : isZero ? alpha("#f44336", 0.04)
        : isYou ? Y_TINT : "background.paper",
      transition: "border-color 0.12s",
      "& .remove-btn": { opacity: { xs: 1, md: 0 }, transition: "opacity 0.15s" },
      "&:hover .remove-btn": { opacity: 1 },
    }}>
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
        bgcolor: isDone
          ? (status === "instant_claimed" ? Y_BROWN : "#2e7d32")
          : isNetLocked ? "#1565c0"
          : isZero ? "#f44336"
          : isYou ? Y : pal.dot }} />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 600, fontSize: "0.88rem", lineHeight: 1.2 }} noWrap>
          {isYou ? "You — agency margin" : seg.name}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap", mt: 0.15 }}>
          {isYou ? (
            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.65rem" }}>
              {isFunded
                ? ledgerClear ? "All advances settled — margin ready to withdraw"
                              : "Waiting for advances to clear"
                : "Credited at settlement"}
            </Typography>
          ) : isDone ? (
            <Typography variant="caption" sx={{
              color: status === "instant_claimed" ? Y_BROWN : "#2e7d32",
              fontSize: "0.65rem", fontWeight: 600 }}>
              {status === "instant_claimed"
                ? `⚡ Paid instantly — received ${fmtCents(seg.netCredited ?? seg.amount * (1 - INSTANT_FEE))}`
                : `✓ Settled in full at net term`}
            </Typography>
          ) : isNetLocked ? (
            <Typography variant="caption" sx={{ color: "#1565c0", fontSize: "0.65rem", fontWeight: 600 }}>
              🔒 Locked to net-{invoicePaymentTerm ?? 30} — they can override to instant from their portal
            </Typography>
          ) : isZero ? (
            <Typography variant="caption" sx={{ color: "#c62828", fontSize: "0.65rem", fontWeight: 600 }}>
              ⚠ Amount required before instant payout can be elected
            </Typography>
          ) : (
            <>
              {seg.email && (
                <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.65rem" }} noWrap>
                  {seg.email}
                </Typography>
              )}
              {showFeePreview && (
                <Typography variant="caption" sx={{ color: Y_BROWN, fontSize: "0.65rem", fontWeight: 600 }}>
                  · {fmtCents(netAmt)} after 5% fee (from invoice advance)
                </Typography>
              )}
            </>
          )}
        </Box>
      </Box>

      <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.68rem",
        minWidth: 28, textAlign: "right", flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
        {pct > 0 ? `${Math.round(pct)}%` : "—"}
      </Typography>

      {/* Status chip — shown for non-done rows OR the net-locked state */}
      {statusMeta && !isDone && (
        <Tooltip title={<Typography variant="caption" sx={{ fontSize: "0.7rem", lineHeight: 1.5 }}>
          {statusMeta.tooltip}</Typography>} placement="top" arrow>
          <Chip label={statusMeta.label} size="small"
            sx={{ height: 18, fontSize: "0.58rem", fontWeight: 700, cursor: "help",
              borderRadius: 1, flexShrink: 0, ...statusMeta.chipSx }} />
        </Tooltip>
      )}

      {/* Action buttons — only shown when canElect (excludes net-locked rows) */}
      {canElect && (
        <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
          <Tooltip title={`Pay ${fmtCents(netAmt)} now — ${feeRate ?? 5}% fee retained from invoice advance, not your balance`}>
            <Button
              size="small"
              variant="contained"
              onClick={() => onElect?.(seg, "instant")}
              disabled={electing}
              startIcon={
                electing
                  ? <CircularProgress size={8} sx={{ color: Y_INK }} />
                  : <BoltIcon sx={{ fontSize: "10px !important" }} />
              }
              sx={{
                height: 24, fontSize: "0.64rem", fontWeight: 700,
                bgcolor: Y, color: Y_INK, borderColor: Y,
                textTransform: "none", px: 0.75, boxShadow: "none",
                "&:hover": { bgcolor: Y_DARK, boxShadow: "none" },
                "&.Mui-disabled": { bgcolor: alpha(Y, 0.3), color: alpha(Y_INK, 0.4) },
              }}
            >
              Pay now
            </Button>
          </Tooltip>
          <Tooltip title={`Lock to net-${invoicePaymentTerm ?? 30} — records your intent. They can still override to instant from their portal.`}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => onElect?.(seg, "wait")}
              disabled={electing}
              sx={{
                height: 24, fontSize: "0.64rem", fontWeight: 700,
                color: "text.secondary", borderColor: "divider",
                textTransform: "none", px: 0.75,
                "&:hover": { borderColor: "#1565c0", color: "#1565c0", bgcolor: alpha("#1565c0", 0.04) },
              }}
            >
              Net-{invoicePaymentTerm ?? 30}
            </Button>
          </Tooltip>
        </Box>
      )}

      {isZero && invoiceStatus === "approved" && (
        <Chip label="Set amount first" size="small"
          icon={<WarningIcon sx={{ fontSize: "10px !important" }} />}
          sx={{ height: 19, fontSize: "0.58rem", fontWeight: 700,
            bgcolor: "#ffebee", color: "#c62828", border: "1px solid #ef9a9a", flexShrink: 0 }} />
      )}

      {canWithdraw && (
        <Tooltip title="All advances cleared — withdraw margin to your Blitz balance">
          <Button size="small" variant="contained"
            onClick={() => onWithdrawMargin?.()}
            disabled={withdrawingMargin}
            startIcon={withdrawingMargin
              ? <CircularProgress size={8} sx={{ color: Y_INK }} />
              : <WithdrawIcon sx={{ fontSize: "11px !important" }} />}
            sx={{ height: 24, fontSize: "0.64rem", fontWeight: 700,
              bgcolor: Y, color: Y_INK, borderRadius: 1.5,
              boxShadow: "none", textTransform: "none", flexShrink: 0, px: 0.75,
              "&:hover": { bgcolor: Y_DARK, boxShadow: "none" } }}>
            Withdraw {fmt(seg.amount)}
          </Button>
        </Tooltip>
      )}
      {withdrawBlocked && (
        <Tooltip title="Open advances must settle before margin releases">
          <Chip label="Advances pending" size="small"
            icon={<LockIcon sx={{ fontSize: "10px !important" }} />}
            sx={{ height: 19, fontSize: "0.58rem", fontWeight: 700,
              bgcolor: "#f5f5f5", color: "#616161", border: "1px solid #e0e0e0",
              flexShrink: 0, cursor: "help" }} />
        </Tooltip>
      )}

      {canRemove && (
        <Tooltip title="Remove from split">
          <IconButton size="small" className="remove-btn"
            onClick={() => onRemove?.(seg)}
            sx={{ flexShrink: 0, color: "text.disabled", "&:hover": { color: "error.main" } }}>
            <DeleteIcon sx={{ fontSize: 13 }} />
          </IconButton>
        </Tooltip>
      )}

      <AmountEditor
        amount={seg.amount}
        locked={!canEditAmount}
        paid={isDone}
        warn={isZero}
        accentColor={isYou ? Y_BROWN : undefined}
        onCommit={(newAmt) => onAmountChange?.(seg, newAmt)}
      />
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────
// ADD PANEL
// ─────────────────────────────────────────────────────────────────
const AddContractorPanel = ({ creatorList, existingSegments, maxAddable, onAdd, onClose, onCSV }) => {
  const csvRef = useRef(null);
  const [q,           setQ]           = useState("");
  const [amount,      setAmount]      = useState("");
  const [blitzpayPre, setBlitzpayPre] = useState(false);
  const [pendingRow,  setPendingRow]  = useState(null);

  const existingNames  = useMemo(
    () => new Set(existingSegments.filter((s) => s.kind !== "you").map((s) => s.name)),
    [existingSegments],
  );
  const existingEmails = useMemo(
    () => new Set(existingSegments.filter((s) => s.email).map((s) => s.email.toLowerCase())),
    [existingSegments],
  );

  const trimmed    = q.trim();
  const looksEmail = trimmed.includes("@") && !trimmed.startsWith("@");
  const isEmail    = isEmailish(trimmed);
  const exactMatch = isEmail ? (creatorList || []).find((c) => c.email?.toLowerCase() === trimmed.toLowerCase()) : null;
  const emailTaken = isEmail && existingEmails.has(trimmed.toLowerCase());
  const showInvite = isEmail && !exactMatch && !emailTaken;

  const results = useMemo(() => {
    if (trimmed.length < 2) return [];
    const lower = trimmed.toLowerCase().replace(/^@/, "");
    return (creatorList || [])
      .filter((c) => !existingNames.has(c.creator) && !existingEmails.has((c.email || "").toLowerCase()))
      .filter((c) =>
        (c.creator || "").toLowerCase().includes(lower) ||
        (c.display_name || "").toLowerCase().includes(lower) ||
        (c.email || "").toLowerCase().includes(lower))
      .slice(0, 6);
  }, [trimmed, creatorList, existingNames, existingEmails]);

  const selectCreator = (creator) => {
    setPendingRow({ kind: "creator", name: creator.creator, email: creator.email, creator });
    setQ(`@${creator.display_name || creator.creator}`);
  };

  const confirmAdd = () => {
    const addAmt = parseFloat(amount) || 0;
    if (pendingRow) onAdd({ ...pendingRow, amount: addAmt, blitzpayPre });
    else if (showInvite) onAdd({ kind: "invite", name: trimmed.split("@")[0], email: trimmed, amount: addAmt, blitzpayPre });
    setQ(""); setAmount(""); setBlitzpayPre(false); setPendingRow(null);
  };

  const personSelected  = pendingRow || (showInvite && trimmed);
  const addAmt          = parseFloat(amount) || 0;
  const feeAmt          = addAmt * INSTANT_FEE;
  const netAmt          = addAmt - feeAmt;
  const budgetAvailable = maxAddable ?? Infinity;
  const overBudget      = addAmt > budgetAvailable && budgetAvailable >= 0;
  const ready           = personSelected && addAmt > 0 && !overBudget;

  return (
    <Box sx={{ p: 1.5, borderRadius: 1.5, border: "0.5px solid", borderColor: Y_BDR, bgcolor: Y_TINT, mt: 1 }}>
      <input ref={csvRef} type="file" accept=".csv,text/csv" hidden
        onChange={(e) => { onCSV?.(e.target.files?.[0]); e.target.value = ""; onClose(); }} />

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.25 }}>
        <Typography sx={{ fontWeight: 700, color: Y_BROWN, textTransform: "uppercase",
          letterSpacing: "0.07em", fontSize: "0.6rem" }}>Add contractor</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Tooltip title="Import from CSV — columns: email, amount">
            <IconButton size="small" onClick={() => csvRef.current?.click()}
              sx={{ color: Y_BROWN, "&:hover": { bgcolor: alpha(Y, 0.2) } }}>
              <CSVIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={onClose}><CloseIcon sx={{ fontSize: 13 }} /></IconButton>
        </Box>
      </Box>

      {budgetAvailable < Infinity && (
        <Box sx={{ px: 1, py: 0.6, mb: 0.75, borderRadius: 1.5,
          bgcolor: budgetAvailable > 0 ? alpha(Y, 0.1) : alpha("#f44336", 0.07),
          border: `0.5px solid ${budgetAvailable > 0 ? Y_BDR : "#ef9a9a"}` }}>
          <Typography variant="caption" sx={{
            color: budgetAvailable > 0 ? Y_BROWN : "#c62828",
            fontSize: "0.65rem", fontWeight: 600 }}>
            {budgetAvailable > 0
              ? `${fmtCents(budgetAvailable)} remaining budget available`
              : "No budget remaining — adjust existing contractor amounts first"}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 0.75 }}>
        <TextField size="small" value={q}
          onChange={(e) => { setQ(e.target.value); setPendingRow(null); }}
          placeholder="Search name, @handle, or email"
          autoFocus
          InputProps={{
            startAdornment: <InputAdornment position="start">
              {looksEmail
                ? <EmailIcon sx={{ fontSize: 13, color: Y_BROWN }} />
                : <SearchIcon sx={{ fontSize: 13, color: "text.disabled" }} />}
            </InputAdornment>,
            sx: { borderRadius: 1.5, fontSize: "0.82rem", bgcolor: "background.paper" },
          }}
          sx={{ flex: 1, minWidth: 160 }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              if (results.length === 1 && !pendingRow) selectCreator(results[0]);
              else if (showInvite && amount) confirmAdd();
            }
          }}
        />
        <TextField size="small" type="number" placeholder="Amount" value={amount}
          onChange={(e) => setAmount(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start">
              <Typography sx={{ fontSize: "0.78rem", color: "text.secondary" }}>$</Typography>
            </InputAdornment>,
            sx: { borderRadius: 1.5, fontSize: "0.82rem", bgcolor: "background.paper" },
          }}
          sx={{ width: 110, flexShrink: 0 }}
        />
      </Box>

      {overBudget && (
        <Box sx={{ px: 1, py: 0.6, mb: 0.75, borderRadius: 1.5,
          bgcolor: alpha("#f44336", 0.07), border: "0.5px solid #ef9a9a" }}>
          <Typography variant="caption" sx={{ color: "#c62828", fontSize: "0.65rem", fontWeight: 600 }}>
            ⚠ Amount exceeds remaining budget ({fmtCents(budgetAvailable)} available). Reduce to add.
          </Typography>
        </Box>
      )}

      {addAmt > 0 && blitzpayPre && !overBudget && (
        <Box sx={{ px: 1, py: 0.6, mb: 0.75, borderRadius: 1.5,
          bgcolor: alpha(Y, 0.12), border: `0.5px solid ${Y_BDR}` }}>
          <Typography variant="caption" sx={{ color: Y_BROWN, fontSize: "0.65rem", fontWeight: 600 }}>
            ⚡ They receive {fmtCents(netAmt)} ({fmtCents(feeAmt)} fee from invoice advance)
          </Typography>
        </Box>
      )}

      {results.length > 0 && !pendingRow && (
        <Stack spacing={0.5} sx={{ mb: 0.75 }}>
          {results.map((c) => (
            <Paper key={c.creator} variant="outlined" onClick={() => selectCreator(c)} sx={{
              p: 0.75, borderRadius: 1.5, cursor: "pointer",
              display: "flex", alignItems: "center", gap: 0.75,
              borderColor: "divider", bgcolor: "background.paper",
              "&:hover": { borderColor: Y_DARK, bgcolor: alpha(Y, 0.02) },
              transition: "all 0.15s",
            }}>
              <Avatar src={c.pfphref} sx={{ width: 30, height: 30, fontSize: "0.72rem", bgcolor: Y_TINT, color: Y_BROWN }}>
                {c.creator?.[0]?.toUpperCase()}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: "0.82rem" }} noWrap>
                  @{c.display_name || c.creator}
                </Typography>
                {c.email && (
                  <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.62rem" }} noWrap>
                    {c.email}
                  </Typography>
                )}
              </Box>
              <Chip label="Add" size="small" sx={{ height: 18, fontSize: "0.58rem", fontWeight: 700, bgcolor: Y, color: Y_INK }} />
            </Paper>
          ))}
        </Stack>
      )}

      {showInvite && !pendingRow && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, px: 1.25, py: 0.75,
          borderRadius: 1.5, bgcolor: alpha(Y, 0.1), border: `0.5px solid ${Y_BDR}`, mb: 0.75,
          cursor: "pointer", "&:hover": { bgcolor: alpha(Y, 0.18) }, transition: "background 0.12s" }}
          onClick={() => setPendingRow({ kind: "invite", name: trimmed.split("@")[0], email: trimmed })}>
          <Box sx={{ width: 26, height: 26, borderRadius: "50%", bgcolor: Y,
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <AddIcon sx={{ fontSize: 13, color: Y_INK }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 600, fontSize: "0.8rem", color: Y_BROWN }}>Invite {trimmed}</Typography>
            <Typography variant="caption" sx={{ color: Y_BROWN, opacity: 0.7, fontSize: "0.63rem" }}>
              They'll get a claim link
            </Typography>
          </Box>
          <Chip label="Select" size="small" sx={{ height: 18, fontSize: "0.58rem", fontWeight: 700, bgcolor: Y, color: Y_INK }} />
        </Box>
      )}

      {emailTaken && (
        <Alert severity="info" sx={{ mb: 0.75, py: 0.25, borderRadius: 1.5 }}>
          <Typography variant="caption" sx={{ fontSize: "0.68rem" }}>{trimmed} is already on this split.</Typography>
        </Alert>
      )}

      {trimmed.length >= 2 && results.length === 0 && !showInvite && !pendingRow && !emailTaken && (
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block",
          textAlign: "center", py: 0.5, fontSize: "0.65rem" }}>
          {looksEmail ? "No creator with that email — select above to invite"
                      : "No match — type a full email to invite someone new"}
        </Typography>
      )}

      {pendingRow && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, px: 1.25, py: 0.75,
          borderRadius: 1.5, bgcolor: "background.paper", border: "0.5px solid",
          borderColor: "divider", mb: 0.75 }}>
          <Avatar src={pendingRow.creator?.pfphref}
            sx={{ width: 26, height: 26, fontSize: "0.68rem", bgcolor: Y_TINT, color: Y_BROWN }}>
            {pendingRow.name?.[0]?.toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 600, fontSize: "0.8rem" }}>{pendingRow.name}</Typography>
            {pendingRow.email && <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.63rem" }}>{pendingRow.email}</Typography>}
          </Box>
          <IconButton size="small" onClick={() => { setPendingRow(null); setQ(""); }}>
            <CloseIcon sx={{ fontSize: 12 }} />
          </IconButton>
        </Box>
      )}

      {personSelected && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
          <Chip
            icon={<BoltIcon sx={{ fontSize: "12px !important",
              color: blitzpayPre ? `${Y_INK} !important` : `${Y_BROWN} !important` }} />}
            label="Pre-accept BlitzPay"
            size="small"
            onClick={() => setBlitzpayPre(!blitzpayPre)}
            sx={{ height: 24, fontSize: "0.63rem", fontWeight: 700, cursor: "pointer",
              bgcolor: blitzpayPre ? Y : "transparent",
              color: blitzpayPre ? Y_INK : Y_BROWN,
              border: "1.5px solid", borderColor: blitzpayPre ? Y : Y_BDR,
              transition: "all 0.15s" }}
          />
          <Typography variant="caption" sx={{ flex: 1, color: "text.secondary", fontSize: "0.62rem" }}>
            {blitzpayPre ? "Instant at approval — fee from invoice advance, not your balance" : "They elect later, or you can elect on their behalf"}
          </Typography>
          <Button variant="contained" size="small" disabled={!ready} onClick={confirmAdd}
            sx={{ fontWeight: 700, textTransform: "none", borderRadius: 1.5,
              bgcolor: Y, color: Y_INK, boxShadow: "none", height: 26, fontSize: "0.7rem",
              "&:hover": { bgcolor: Y_DARK, boxShadow: "none" },
              "&.Mui-disabled": { bgcolor: alpha(Y, 0.3), color: alpha(Y_INK, 0.4) } }}>
            Add to split
          </Button>
        </Box>
      )}

      <Box onClick={() => csvRef.current?.click()} sx={{
        mt: 1.25, p: 0.75, borderRadius: 1.5, border: "1px dashed",
        borderColor: alpha(Y, 0.35), cursor: "pointer",
        display: "flex", alignItems: "center", gap: 0.75,
        "&:hover": { borderColor: Y_DARK, bgcolor: alpha(Y, 0.04) },
        transition: "all 0.15s" }}>
        <CSVIcon sx={{ fontSize: 13, color: Y_BROWN }} />
        <Typography variant="caption" sx={{ fontWeight: 700, color: Y_BROWN, flex: 1, fontSize: "0.63rem" }}>
          Adding a roster? Import a CSV
        </Typography>
        <Typography variant="caption" sx={{ color: "text.disabled", fontSize: "0.6rem" }}>email · amount</Typography>
        <UploadIcon sx={{ fontSize: 12, color: Y_BROWN }} />
      </Box>
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════
const BlitzSplitManageDialog = ({ invoice: invoiceProp, onClose, onSuccess }) => {
  const theme    = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [invoice,            setInvoice]            = useState(null);
  const [payments,           setPayments]            = useState([]);
  const [bbcSummary,         setBbcSummary]          = useState(null);
  const [creatorList,        setCreatorList]          = useState([]);
  const [loading,            setLoading]              = useState(false);
  const [converting,         setConverting]           = useState(false);
  const [convertConfirmOpen, setConvertConfirmOpen]   = useState(false);
  const [electingSingle,     setElectingSingle]       = useState(null);
  const [withdrawingMargin,  setWithdrawingMargin]    = useState(false);
  const [error,              setError]                = useState(null);
  const [successMsg,         setSuccessMsg]           = useState(null);
  const [showAdd,            setShowAdd]              = useState(false);
  const [removePending,      setRemovePending]        = useState(null);
  const [csvMsg,             setCsvMsg]               = useState(null);
  const [ledgerRemaining,    setLedgerRemaining]      = useState(null);
  const [isDirty,            setIsDirty]              = useState(false);
  const [saving,             setSaving]               = useState(false);
  const [showMarginDraw,     setShowMarginDraw]       = useState(false);
  const [marginDrawAmt,      setMarginDrawAmt]        = useState("");

  // ── Net-term confirm dialog state (NEW v10.3) ──────────────────
  const [netTermPending,     setNetTermPending]       = useState(null); // { seg } | null
  const [netTermSaving,      setNetTermSaving]        = useState(false);

  // Derived
  const inv            = invoice || invoiceProp || {};
  const total          = parseFloat(inv.amount_due || inv.amount || 0);
  const contractorSum  = payments.reduce((s, p) => s + parseFloat(p.amount || 0), 0);
  const margin         = Math.max(0, total - contractorSum);
  const enterpriseName = inv.client_name  || invoiceProp?.client_name  || "Enterprise";
  const poNumber       = inv.po_number    || invoiceProp?.po_number    || "—";
  const paymentTerm    = inv.payment_term ?? invoiceProp?.payment_term;
  const invStatus      = (inv.status || invoiceProp?.status || "pending").toLowerCase();
  const bbcId          = bbcSummary?.bbc_id || inv.custom_details?.bbc_id;
  const feeRate        = bbcSummary?.instant_fee_rate ?? inv.custom_details?.instant_fee_rate ?? 5;

  // Expected settle date (for the net-term confirm dialog)
  const approvedRaw    = inv?.approved_at || inv?.date_approved;
  const approvedDate   = approvedRaw ? parseISO(approvedRaw) : null;
  const expectedSettle = approvedDate && isValid(approvedDate)
    ? addDays(approvedDate, parseInt(paymentTerm) || 30) : null;

  const promised     = payments.filter((p) => (p.status || "promised") === "promised");
  const instantPaid  = payments.filter((p) => p.status === "instant_claimed");
  const settled      = payments.filter((p) => p.status === "settled");
  const instantSum   = instantPaid.reduce((s, p) => s + parseFloat(p.amount || 0), 0);
  const settledSum   = settled.reduce((s, p) => s + parseFloat(p.amount || 0), 0);
  const pendingSum   = promised.reduce((s, p) => s + parseFloat(p.amount || 0), 0);
  const disbursedAny = instantPaid.length > 0 || settled.length > 0;

  const canBulk     = invStatus === "approved" && promised.filter(p => p.decision !== "wait").length > 0 && !!bbcId;
  const statusCfg   = INV_STATUS_CFG[invStatus] || INV_STATUS_CFG.pending;
  const zeroCount   = payments.filter((p) => parseFloat(p.amount || 0) === 0).length;
  const ledgerClear = ledgerRemaining !== null && ledgerRemaining <= 0;

  const baseSegments = useMemo(() => [
    { id: "you", kind: "you", name: "You", amount: margin },
    ...payments.map((p, i) => ({
      id:          p.id ?? i,
      kind:        "contractor",
      name:        p.recipient_name || p.creator_id || p.recipient_email || "Contractor",
      email:       p.recipient_email,
      amount:      parseFloat(p.amount || 0),
      status:      p.status,
      decision:    p.decision,           // ← pass decision down
      preapproved: p.preapproved || p.blitzpay_preapproved,
      netCredited: p.net_credited ? parseFloat(p.net_credited) : null,
    })),
  ], [margin, payments]);

  const segments = baseSegments;

  const fetchData = useCallback(async () => {
    if (!invoiceProp?.id) return;
    setLoading(true); setError(null);
    try {
      const [data, creators] = await Promise.all([
        client.invoices.fetch(invoiceProp.id),
        client.creators.list().catch(() => []),
      ]);
      const fullInv = data?.invoice || data;
      setInvoice(fullInv);
      const roster =
        data?.creator_payments?.length ? data.creator_payments :
        data?.payouts?.length          ? data.payouts           : [];
      setPayments(roster);
      if (data?.bbc_summary) setBbcSummary(data.bbc_summary);
      setCreatorList((creators || []).map((c) => ({ ...c, label: c.creator })));
      setIsDirty(false);

      const bid = data?.bbc_summary?.bbc_id || fullInv?.custom_details?.bbc_id;
      if (bid) {
        client.bbc.getLedgerRemaining?.(bid)
          .then((r) => setLedgerRemaining(parseFloat(r?.remaining_after ?? r?.remaining ?? 1)))
          .catch(() => setLedgerRemaining(null));
      }
    } catch {
      setInvoice(invoiceProp);
    } finally {
      setLoading(false);
    }
  }, [invoiceProp?.id]);

  useEffect(() => {
    setInvoice(null); setPayments([]); setBbcSummary(null);
    setError(null); setSuccessMsg(null); setLedgerRemaining(null);
    fetchData();
  }, [invoiceProp?.id]);

  if (!invoiceProp) return null;
  const handleClose = onClose || (() => navigate(-1));

  // ── Amount change ──────────────────────────────────────────────── //
  const handleAmountChange = (seg, newAmt) => {
    setPayments((prev) => {
      const otherSum = prev.reduce((s, p) => {
        const name = p.recipient_name || p.creator_id || p.recipient_email || "Contractor";
        return name === seg.name ? s : s + parseFloat(p.amount || 0);
      }, 0);
      const maxForRow = Math.max(0, total - otherSum);
      const clamped   = Math.min(newAmt, maxForRow);
      return prev.map((p) => {
        const name = p.recipient_name || p.creator_id || p.recipient_email || "Contractor";
        return name !== seg.name ? p : { ...p, amount: clamped };
      });
    });
    setIsDirty(true);
  };

  // ── Bulk convert ───────────────────────────────────────────────── //
  const handleBulkConvertRequest = () => setConvertConfirmOpen(true);

  const handleBulkConvertConfirm = async () => {
    setConvertConfirmOpen(false);
    if (!bbcId) { setError("BBC ID not found — try refreshing"); return; }
    setConverting(true); setError(null);
    try {
      const res       = await client.bbc.convertToBlitzPay(bbcId);
      const converted = res?.converted?.length ?? 0;
      setSuccessMsg(converted > 0
        ? `${converted} contractor${converted !== 1 ? "s" : ""} converted to instant.`
        : res?.message || "Conversion complete.");
      onSuccess?.(res?.message || "Instant payouts elected.");
      await fetchData();
    } catch (err) {
      setError(err?.response?.data?.error || "Conversion failed — try refreshing.");
    } finally {
      setConverting(false);
    }
  };

  // ── Single elect ───────────────────────────────────────────────── //
  // v10.3: 'wait' now opens a confirm dialog before any API call.
  // On confirm it calls client.bbc.decide(seg.id, 'wait').
  // Local state is updated optimistically so the row re-renders immediately.
  const handleElectSingle = async (seg, decision = "instant") => {
    if (decision === "wait") {
      // Open the net-term confirm dialog — do NOT call the API yet
      setNetTermPending({ seg });
      return;
    }

    // Instant path — unchanged
    if (!bbcId) { setError("BBC ID not found"); return; }
    setElectingSingle(seg.id); setError(null);
    try {
      const res = await client.bbc.decide(seg.id, decision);
      setSuccessMsg(`⚡ Instant payout sent to ${seg.name}.`);
      onSuccess?.(res?.message || "Decision recorded.");
      await fetchData();
    } catch (err) {
      setError(err?.response?.data?.error || "Could not record decision.");
    } finally {
      setElectingSingle(null);
    }
  };

  // ── Net-term confirm ───────────────────────────────────────────── //
  const handleNetTermConfirm = async () => {
    if (!netTermPending?.seg) return;
    const seg = netTermPending.seg;
    setNetTermSaving(true); setError(null);
    try {
      await client.bbc.decide(seg.id, "wait");
      // Update local payments state to reflect decision='wait'
      // so the row immediately shows the locked treatment
      setPayments((prev) => prev.map((p) => {
        const name = p.recipient_name || p.creator_id || p.recipient_email || "Contractor";
        return name !== seg.name ? p : { ...p, decision: "wait" };
      }));
      setSuccessMsg(`${seg.name} locked to net-${paymentTerm ?? 30}. They can still elect instant from their portal.`);
      setNetTermPending(null);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not record net-term preference.");
    } finally {
      setNetTermSaving(false);
    }
  };

  const handleNetTermCancel = () => setNetTermPending(null);

  // ── Remove ─────────────────────────────────────────────────────── //
  const handleRemoveRequest = (seg) => setRemovePending(seg);

  const handleRemoveConfirm = async () => {
    if (!removePending) return;
    setPayments((prev) => prev.filter((p) => {
      const name = p.recipient_name || p.creator_id || p.recipient_email || "Contractor";
      return name !== removePending.name;
    }));
    const paymentId  = removePending.id;
    const isLocalRow = typeof paymentId === "string" && paymentId.startsWith("local_");
    if (!isLocalRow && paymentId) {
      try { await client.bbc.cancelPayment(paymentId); }
      catch (err) { console.warn("[BlitzSplit] cancelPayment failed:", err); }
    }
    setRemovePending(null);
    setIsDirty(true);
  };

  // ── Add ─────────────────────────────────────────────────────────── //
  const handleAdd = ({ name, email, kind, creator, amount: addAmt, blitzpayPre }) => {
    const clamped = Math.min(addAmt || 0, Math.max(0, margin));
    setPayments((prev) => [...prev, {
      id:                   `local_${Date.now()}`,
      recipient_name:       name,
      recipient_email:      email,
      creator_id:           creator?.creator,
      amount:               clamped,
      status:               "promised",
      decision:             null,
      preapproved:          !!blitzpayPre,
      blitzpay_preapproved: !!blitzpayPre,
    }]);
    setShowAdd(false);
    setIsDirty(true);
  };

  // ── CSV ─────────────────────────────────────────────────────────── //
  const handleCSV = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const parsed = parseCSV(String(e.target.result || ""));
      if (!parsed.length) {
        setCsvMsg({ severity: "warning", text: "Couldn't read rows — expected columns: email, amount." });
        return;
      }
      const existingEmails = new Set(payments.filter((p) => p.recipient_email)
        .map((p) => p.recipient_email.toLowerCase()));
      let added = 0, skipped = 0, overBudgetCount = 0;
      const next = [];
      let runningMargin = margin;
      parsed.forEach((row) => {
        if (existingEmails.has(row.email.toLowerCase())) { skipped++; return; }
        const match       = (creatorList || []).find((c) => c.email?.toLowerCase() === row.email.toLowerCase());
        const clampedAmt  = Math.min(row.amount || 0, Math.max(0, runningMargin));
        if (row.amount > 0 && clampedAmt < row.amount) overBudgetCount++;
        runningMargin -= clampedAmt;
        next.push({
          id: `local_csv_${Date.now()}_${added}`,
          recipient_name:  match ? match.creator : row.email.split("@")[0],
          recipient_email: row.email,
          creator_id:      match?.creator,
          amount:          clampedAmt,
          status:          "promised",
          decision:        null,
          preapproved:     false,
        });
        existingEmails.add(row.email.toLowerCase());
        added++;
      });
      setPayments((prev) => [...prev, ...next]);
      setIsDirty(true);
      setCsvMsg({
        severity: overBudgetCount > 0 ? "warning" : "success",
        text: `Imported ${added} contractor${added !== 1 ? "s" : ""}${skipped ? ` · ${skipped} skipped` : ""}${overBudgetCount > 0 ? ` · ${overBudgetCount} capped to budget` : ""}.`,
      });
    };
    reader.readAsText(file);
  };

  // ── Withdraw margin ────────────────────────────────────────────── //
  const handleWithdrawMargin = async () => {
    if (!bbcId) { setError("BBC ID not found"); return; }
    setWithdrawingMargin(true); setError(null);
    try {
      const res = await client.bbc.withdrawMargin(bbcId, { amount: margin });
      setSuccessMsg(res?.message || `${fmt(margin)} credited to your Blitz balance.`);
      onSuccess?.(res?.message || "Margin withdrawn.");
      await fetchData();
    } catch (err) {
      setError(err?.response?.data?.error || "Could not withdraw margin.");
    } finally {
      setWithdrawingMargin(false);
    }
  };

  // ── Save ───────────────────────────────────────────────────────── //
  const handleSave = async () => {
    if (!bbcId && !invoiceProp?.id) { setError("No invoice ID — can't save"); return; }
    setSaving(true); setError(null);
    try {
      const updates = payments.map((p) => ({
        id:              p.id,
        amount:          parseFloat(p.amount || 0),
        preapproved:     !!(p.preapproved || p.blitzpay_preapproved),
        recipient_email: p.recipient_email || null,
        recipient_name:  p.recipient_name  || p.creator_id || null,
        creator_id:      p.creator_id      || null,
      }));
      const res = await client.bbc.updatePayments(bbcId || invoiceProp.id, { payments: updates });
      if (res?.payments?.length) setPayments(res.payments);
      if (res?.sme_margin !== undefined) {
        setBbcSummary((prev) => prev ? { ...prev, sme_margin: res.sme_margin } : prev);
      }
      setIsDirty(false);
      setSuccessMsg("Changes saved.");
      onSuccess?.(res?.message || "Roster saved.");
    } catch (err) {
      setError(err?.response?.data?.error || "Save failed — check your connection.");
    } finally {
      setSaving(false);
    }
  };

  // ── Margin draw ────────────────────────────────────────────────── //
  const handleMarginDraw = async () => {
    const drawAmt = parseFloat(marginDrawAmt) || 0;
    if (drawAmt <= 0 || drawAmt > margin) {
      setError(`Draw amount must be between ${fmt(1)} and ${fmt(margin)}`);
      return;
    }
    if (!bbcId) { setError("BBC ID not found"); return; }
    setWithdrawingMargin(true); setError(null);
    try {
      const res = await client.bbc.withdrawMargin(bbcId, { amount: drawAmt });
      setSuccessMsg(res?.message || `${fmt(drawAmt)} drawn to your Blitz balance.`);
      setShowMarginDraw(false); setMarginDrawAmt("");
      onSuccess?.(res?.message || "Margin drawn.");
      await fetchData();
    } catch (err) {
      setError(err?.response?.data?.error || "Draw failed.");
    } finally {
      setWithdrawingMargin(false);
    }
  };

  // ── Status banner ──────────────────────────────────────────────── //
  const statusBanner = invStatus === "pending" ? {
    severity: "info",
    text: `Waiting for ${enterpriseName} to approve. Amounts are editable — set contractor slices now.`,
  } : invStatus === "approved" ? {
    severity: "warning",
    text: `Invoice approved by ${enterpriseName}. Instant payouts are unlocked — the BlitzPay fee is retained from the invoice advance, not your balance.`,
  } : (invStatus === "paid" || invStatus === "funded") ? {
    severity: "success",
    text: `Funding complete.${margin > 0 ? ` Your margin (${fmt(margin)}) is ${ledgerClear ? "ready to withdraw." : "pending — advances must settle first."}` : ""}`,
  } : null;

  // ─────────────────────────────────────────────────────────────────
  // SUB-COMPONENTS
  // ─────────────────────────────────────────────────────────────────

  const PayoutsSection = () => (
    <>
      {["paid", "funded"].includes(invStatus) && (
        <Box sx={{ px: 1.25, py: 0.85, mb: 1.25, borderRadius: 1.5,
          bgcolor: alpha("#2e7d32", 0.06), border: "0.5px solid #a5d6a7",
          display: "flex", alignItems: "center", gap: 0.75 }}>
          <LockIcon sx={{ fontSize: 13, color: "#2e7d32" }} />
          <Typography variant="caption" sx={{ color: "#1b5e20", fontSize: "0.68rem", fontWeight: 600 }}>
            Invoice funded — paid rows are permanently locked. Only pending rows can be edited.
          </Typography>
        </Box>
      )}

      {disbursedAny && (
        <Box sx={{ px: 1.25, py: 1, mb: 1.25, borderRadius: 1.5,
          bgcolor: Y_TINT, border: `0.5px solid ${Y_BDR}` }}>
          <Typography sx={{ fontWeight: 700, fontSize: "0.6rem", textTransform: "uppercase",
            letterSpacing: "0.08em", color: Y_BROWN, mb: 0.75 }}>💸 Money sent</Typography>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            {instantSum > 0 && (
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: "0.9rem", color: Y_BROWN,
                  fontVariantNumeric: "tabular-nums" }}>{fmt(instantSum)}</Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.6rem" }}>
                  ⚡ Instant paid ({instantPaid.length} contractor{instantPaid.length !== 1 ? "s" : ""})
                </Typography>
              </Box>
            )}
            {settledSum > 0 && (
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: "0.9rem", color: "#2e7d32",
                  fontVariantNumeric: "tabular-nums" }}>{fmt(settledSum)}</Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.6rem" }}>
                  ✓ Settled ({settled.length})
                </Typography>
              </Box>
            )}
            {pendingSum > 0 && (
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: "0.9rem",
                  fontVariantNumeric: "tabular-nums" }}>{fmt(pendingSum)}</Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.6rem" }}>
                  Pending net-{paymentTerm ?? 30}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      <Box sx={{ display: "flex", alignItems: "center", mb: 1.25, gap: 0.75 }}>
        <Typography sx={{ fontWeight: 700, fontSize: "0.6rem", textTransform: "uppercase",
          letterSpacing: "0.08em", color: "text.secondary" }}>
          Payouts · {segments.length}
        </Typography>
        <Tooltip title={<Typography variant="caption" sx={{ fontSize: "0.7rem", lineHeight: 1.5 }}>
          Click any amount field to edit. Paid rows (green ✓) are locked.
          "You" is your agency margin — it updates live as you adjust contractor amounts.
          Net-term locked rows (🔒) can only be overridden by the contractor from their portal.
        </Typography>} arrow placement="right">
          <InfoIcon sx={{ fontSize: 13, color: "text.disabled", cursor: "help" }} />
        </Tooltip>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6 }}>
        {segments.map((seg, i) => (
          <PayoutRow
            key={seg.id}
            seg={seg}
            total={total}
            palIdx={i - 1}
            invoiceStatus={invStatus}
            invoicePaymentTerm={paymentTerm}
            feeRate={feeRate}
            onElect={handleElectSingle}
            electing={electingSingle === seg.id}
            onRemove={handleRemoveRequest}
            onAmountChange={handleAmountChange}
            onWithdrawMargin={handleWithdrawMargin}
            withdrawingMargin={withdrawingMargin}
            ledgerClear={ledgerClear}
          />
        ))}
      </Box>

      <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1.5, bgcolor: "action.hover" }}>
        <Typography sx={{ fontWeight: 700, fontSize: "0.6rem", textTransform: "uppercase",
          letterSpacing: "0.08em", color: "text.secondary", mb: 1 }}>Budget</Typography>
        <BudgetTracker
          total={total}
          instantPaidSum={instantSum}
          pendingSum={pendingSum}
          margin={margin}
          unallocated={Math.max(0, total - contractorSum - margin)}
        />
        {margin < 0 && (
          <Alert severity="error" sx={{ mt: 0.5, borderRadius: 1.5, py: 0.4 }}>
            <Typography variant="caption" sx={{ fontSize: "0.72rem" }}>
              Contractor amounts exceed invoice total by {fmt(Math.abs(margin))}. Reduce someone's slice.
            </Typography>
          </Alert>
        )}
      </Box>

      {margin > 0 && ["paid", "funded"].includes(invStatus) && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <SplitIcon sx={{ fontSize: 14, color: Y_BROWN }} />
              <Typography sx={{ fontWeight: 700, fontSize: "0.6rem", textTransform: "uppercase",
                letterSpacing: "0.08em", color: Y_BROWN }}>Draw from margin</Typography>
            </Box>
            <Button size="small" variant="text"
              onClick={() => setShowMarginDraw(!showMarginDraw)}
              startIcon={showMarginDraw
                ? <CloseIcon sx={{ fontSize: "11px !important" }} />
                : <SplitIcon sx={{ fontSize: "11px !important" }} />}
              sx={{ fontWeight: 700, textTransform: "none",
                color: showMarginDraw ? "text.secondary" : Y_BROWN,
                fontSize: "0.7rem", "&:hover": { bgcolor: Y_TINT } }}>
              {showMarginDraw ? "Cancel" : "Draw a segment"}
            </Button>
          </Box>
          <Collapse in={showMarginDraw}>
            <Box sx={{ p: 1.5, borderRadius: 1.5, border: `0.5px solid ${Y_BDR}`, bgcolor: Y_TINT }}>
              <Typography variant="caption" sx={{ color: Y_BROWN, fontSize: "0.68rem",
                fontWeight: 600, display: "block", mb: 1 }}>
                Pull a portion of your {fmt(margin)} margin to your Blitz balance now.
                {!ledgerClear && " Some advances are still open — cleared portion only."}
              </Typography>
              <Box sx={{ display: "flex", gap: 0.5, mb: 1, flexWrap: "wrap" }}>
                {[25, 50, 75, 100].map((pctVal) => {
                  const amt = Math.floor(margin * pctVal / 100);
                  return (
                    <Chip key={pctVal} label={`${pctVal}% · ${fmt(amt)}`} size="small"
                      onClick={() => setMarginDrawAmt(String(amt))}
                      sx={{ height: 22, fontSize: "0.62rem", fontWeight: 700, cursor: "pointer",
                        bgcolor: marginDrawAmt === String(amt) ? Y : "background.paper",
                        color: marginDrawAmt === String(amt) ? Y_INK : Y_BROWN,
                        border: `1px solid ${Y_BDR}`, "&:hover": { bgcolor: alpha(Y, 0.3) } }} />
                  );
                })}
              </Box>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <TextField size="small" type="number" placeholder="Custom amount"
                  value={marginDrawAmt} onChange={(e) => setMarginDrawAmt(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">
                      <Typography sx={{ fontSize: "0.78rem", color: "text.secondary" }}>$</Typography>
                    </InputAdornment>,
                    sx: { borderRadius: 1.5, fontSize: "0.85rem", fontWeight: 700, bgcolor: "background.paper" },
                  }}
                  sx={{ flex: 1 }} />
                <Button variant="contained" size="small"
                  disabled={withdrawingMargin || !marginDrawAmt || parseFloat(marginDrawAmt) <= 0 || parseFloat(marginDrawAmt) > margin}
                  onClick={handleMarginDraw}
                  startIcon={withdrawingMargin
                    ? <CircularProgress size={9} sx={{ color: Y_INK }} />
                    : <WithdrawIcon sx={{ fontSize: "11px !important" }} />}
                  sx={{ fontWeight: 700, textTransform: "none", bgcolor: Y, color: Y_INK,
                    borderRadius: 1.5, boxShadow: "none", height: 32, fontSize: "0.72rem", flexShrink: 0,
                    "&:hover": { bgcolor: Y_DARK, boxShadow: "none" },
                    "&.Mui-disabled": { bgcolor: alpha(Y, 0.3), color: alpha(Y_INK, 0.4) } }}>
                  {withdrawingMargin ? "Drawing…" : `Draw ${marginDrawAmt ? fmt(parseFloat(marginDrawAmt) || 0) : "—"}`}
                </Button>
              </Box>
              {marginDrawAmt && parseFloat(marginDrawAmt) > margin && (
                <Typography variant="caption" sx={{ color: "error.main", fontSize: "0.65rem", mt: 0.5, display: "block" }}>
                  Can't draw more than your margin ({fmt(margin)}).
                </Typography>
              )}
              {marginDrawAmt && parseFloat(marginDrawAmt) > 0 && parseFloat(marginDrawAmt) <= margin && (
                <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.65rem", mt: 0.5, display: "block" }}>
                  Remaining after draw: {fmt(margin - (parseFloat(marginDrawAmt) || 0))}
                </Typography>
              )}
            </Box>
          </Collapse>
        </Box>
      )}

      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "0.6rem", textTransform: "uppercase",
            letterSpacing: "0.08em", color: "text.secondary" }}>Add contractor</Typography>
          <Button size="small" variant="text"
            onClick={() => setShowAdd(!showAdd)}
            startIcon={showAdd
              ? <CloseIcon sx={{ fontSize: "11px !important" }} />
              : <AddIcon sx={{ fontSize: "11px !important" }} />}
            sx={{ fontWeight: 700, textTransform: "none",
              color: showAdd ? "text.secondary" : Y_BROWN,
              fontSize: "0.7rem", "&:hover": { bgcolor: Y_TINT } }}>
            {showAdd ? "Cancel" : "Add"}
          </Button>
        </Box>
        <Collapse in={showAdd}>
          <AddContractorPanel
            creatorList={creatorList}
            existingSegments={segments}
            maxAddable={margin}
            onAdd={handleAdd}
            onClose={() => setShowAdd(false)}
            onCSV={handleCSV}
          />
        </Collapse>
        {!showAdd && (
          <Box onClick={() => setShowAdd(true)} sx={{
            p: 1, borderRadius: 1.5, border: "1px dashed",
            borderColor: alpha(Y, 0.35), cursor: "pointer",
            display: "flex", alignItems: "center", gap: 0.75,
            "&:hover": { borderColor: Y_DARK, bgcolor: alpha(Y, 0.03) },
            transition: "all 0.15s" }}>
            <AddIcon sx={{ fontSize: 14, color: Y_BROWN }} />
            <Typography variant="caption" sx={{ fontWeight: 600, color: Y_BROWN, fontSize: "0.68rem" }}>
              Add contractor, invite by email, or import a CSV roster
            </Typography>
          </Box>
        )}
      </Box>
    </>
  );

  const SummarySection = () => (
    <>
      <Box>
        <Typography sx={{ fontWeight: 500, fontSize: "0.6rem", textTransform: "uppercase",
          letterSpacing: "0.1em", color: Y_BROWN, mb: 0.5 }}>Invoice total</Typography>
        <Typography sx={{ fontWeight: 700, fontSize: isMobile ? "2.2rem" : "3rem",
          lineHeight: 1, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em" }}>
          {fmt(total)}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.68rem", mt: 0.4, display: "block" }}>
          {payments.length} contractor{payments.length !== 1 ? "s" : ""} · {termLabel(paymentTerm)}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        {[
          { label: "Instant paid",             value: fmt(instantSum), amber:   true, show: instantSum > 0 },
          { label: `Net-${paymentTerm ?? 30}`, value: fmt(pendingSum),                show: pendingSum > 0 },
          { label: "Settled",                  value: fmt(settledSum), success: true, show: settledSum > 0 },
          { label: "Your margin",              value: fmt(margin),     success: true, show: margin > 0 },
        ].filter((s) => s.show).map(({ label, value, amber, success }) => (
          <Box key={label} sx={{ flex: 1, minWidth: 72, p: "8px 10px", borderRadius: 1.5, bgcolor: "action.hover" }}>
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, fontVariantNumeric: "tabular-nums",
              color: success ? "success.main" : amber ? Y_BROWN : "text.primary", lineHeight: 1.2 }}>
              {value}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.6rem" }}>{label}</Typography>
          </Box>
        ))}
      </Box>

      {!isMobile && <Box sx={{ height: "0.5px", bgcolor: "divider" }} />}
      {!isMobile && (
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: "0.6rem", textTransform: "uppercase",
            letterSpacing: "0.08em", color: "text.secondary", mb: 1 }}>
            Net-{paymentTerm ?? 30} countdown
          </Typography>
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "action.hover" }}>
            <DosageDial inv={inv} bbcSummary={bbcSummary} payments={payments} />
          </Box>
        </Box>
      )}

      {zeroCount > 0 && (
        <Alert severity="error" icon={<WarningIcon fontSize="small" />} sx={{ borderRadius: 1.5, py: 0.5 }}>
          <Typography sx={{ fontSize: "0.78rem", fontWeight: 600 }}>
            {zeroCount} contractor{zeroCount !== 1 ? "s have" : " has"} no amount set
          </Typography>
        </Alert>
      )}

      {csvMsg && (
        <Alert severity={csvMsg.severity} sx={{ borderRadius: 1.5 }} onClose={() => setCsvMsg(null)}>
          <Typography sx={{ fontSize: "0.78rem" }}>{csvMsg.text}</Typography>
        </Alert>
      )}
      {statusBanner && (
        <Alert severity={statusBanner.severity} sx={{ borderRadius: 1.5, py: 0.5 }}>
          <Typography sx={{ fontSize: "0.78rem", lineHeight: 1.5 }}>{statusBanner.text}</Typography>
        </Alert>
      )}
      {successMsg && (
        <Alert severity="success" sx={{ borderRadius: 1.5 }} onClose={() => setSuccessMsg(null)}>
          <Typography sx={{ fontSize: "0.78rem" }}>{successMsg}</Typography>
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ borderRadius: 1.5 }} onClose={() => setError(null)}>
          <Typography sx={{ fontSize: "0.78rem" }}>{error}</Typography>
        </Alert>
      )}
    </>
  );

  const TopBar = () => (
    <Box sx={{ px: { xs: 2, md: 4 }, py: 1.25, display: "flex", alignItems: "center",
      justifyContent: "space-between", borderBottom: "0.5px solid", borderColor: "divider",
      bgcolor: "background.paper", flexShrink: 0, gap: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
        <IconButton size="small" onClick={handleClose} disabled={converting}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Box sx={{ display: { xs: "none", sm: "inline-flex" }, alignItems: "center", gap: 0.5,
          px: 1, py: 0.3, borderRadius: 999, bgcolor: Y_TINT, border: `0.5px solid ${Y_BDR}`, flexShrink: 0 }}>
          <BoltIcon sx={{ fontSize: 10, color: Y_BROWN }} />
          <Typography sx={{ fontWeight: 800, color: Y_BROWN, fontSize: "0.64rem", letterSpacing: "0.04em" }}>Split</Typography>
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: { xs: "0.85rem", md: "0.95rem" }, lineHeight: 1.15 }} noWrap>
            {enterpriseName}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.62rem" }}>
            {poNumber} · {termLabel(paymentTerm)}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
        <Chip
          icon={React.cloneElement(statusCfg.icon, { sx: { fontSize: "10px !important" } })}
          label={isMobile ? undefined : statusCfg.label}
          size="small" color={statusCfg.color}
          sx={{ height: 21, fontSize: "0.64rem", minWidth: isMobile ? 21 : undefined }}
        />
        {isDirty && (
          <Button variant="contained" size="small" disabled={saving}
            onClick={handleSave}
            startIcon={saving
              ? <CircularProgress size={10} sx={{ color: "white" }} />
              : <SaveIcon sx={{ fontSize: "11px !important" }} />}
            sx={{ fontWeight: 700, textTransform: "none", bgcolor: "success.main",
              color: "white", borderRadius: 1.5, boxShadow: "none", height: 26, fontSize: "0.7rem",
              "&:hover": { bgcolor: "success.dark", boxShadow: "none" },
              animation: "pulse-save 1.5s ease-in-out infinite",
              "@keyframes pulse-save": {
                "0%, 100%": { boxShadow: "0 0 0 0 rgba(46,125,50,0.4)" },
                "50%": { boxShadow: "0 0 0 6px rgba(46,125,50,0)" },
              } }}>
            {saving ? "Saving…" : isMobile ? "Save" : "Save changes"}
          </Button>
        )}
        {canBulk && !isMobile && (
          <Button variant="contained" size="small" disabled={converting}
            onClick={handleBulkConvertRequest}
            startIcon={converting
              ? <CircularProgress size={10} sx={{ color: Y_INK }} />
              : <BoltIcon sx={{ fontSize: "11px !important" }} />}
            sx={{ fontWeight: 700, textTransform: "none", bgcolor: Y, color: Y_INK,
              borderRadius: 1.5, boxShadow: "none", height: 26, fontSize: "0.7rem",
              "&:hover": { bgcolor: Y_DARK, boxShadow: "none" },
              "&.Mui-disabled": { bgcolor: alpha(Y, 0.3), color: alpha(Y_INK, 0.4) } }}>
            {converting ? "Converting…" : `Convert ${promised.filter(p => p.decision !== "wait").length} → instant`}
          </Button>
        )}
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={fetchData} disabled={loading}>
            <RefreshIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
        {!isMobile && (
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon sx={{ fontSize: 15 }} />
          </IconButton>
        )}
      </Box>
    </Box>
  );

  // ─────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ position: "fixed", inset: 0, bgcolor: "background.default",
      zIndex: theme.zIndex.modal, display: "flex", flexDirection: "column", overflow: "hidden" }}>

      <RemoveConfirmDialog
        seg={removePending}
        onConfirm={handleRemoveConfirm}
        onCancel={() => setRemovePending(null)}
      />
      <ConvertConfirmDialog
        open={convertConfirmOpen}
        promisedRows={promised.filter(p => p.decision !== "wait")}
        feeRate={feeRate}
        onConfirm={handleBulkConvertConfirm}
        onCancel={() => setConvertConfirmOpen(false)}
      />
      {/* NET-TERM CONFIRM DIALOG — new in v10.3 */}
      <NetTermConfirmDialog
        seg={netTermPending?.seg || null}
        paymentTerm={paymentTerm}
        settleDate={expectedSettle}
        onConfirm={handleNetTermConfirm}
        onCancel={handleNetTermCancel}
        saving={netTermSaving}
      />

      <TopBar />
      {loading && (
        <LinearProgress sx={{ height: 2, flexShrink: 0,
          bgcolor: alpha(Y, 0.15), "& .MuiLinearProgress-bar": { bgcolor: Y } }} />
      )}

      {isMobile ? (
        <>
          <Box sx={{ flex: 1, overflowY: "auto", px: 2, pt: 2,
            pb: canBulk ? "calc(170px + env(safe-area-inset-bottom, 0px))"
                        : "calc(104px + env(safe-area-inset-bottom, 0px))" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              <SummarySection />
              <Box sx={{ height: "0.5px", bgcolor: "divider" }} />
              <PayoutsSection />
            </Box>
          </Box>
          {canBulk && (
            <Box sx={{ position: "fixed", bottom: 0, left: 0, right: 0,
              px: 2, pt: 1.5, pb: "calc(98px + env(safe-area-inset-bottom, 0px))",
              bgcolor: "background.paper", borderTop: "0.5px solid",
              borderColor: "divider", boxShadow: "0 -2px 12px rgba(0,0,0,0.06)" }}>
              <Button fullWidth variant="contained" size="large"
                disabled={converting}
                onClick={handleBulkConvertRequest}
                startIcon={converting ? <CircularProgress size={18} sx={{ color: Y_INK }} /> : <BoltIcon />}
                sx={{ fontWeight: 800, textTransform: "none", bgcolor: Y, color: Y_INK,
                  borderRadius: 2, height: 50, fontSize: "0.9rem",
                  boxShadow: `0 4px 16px ${alpha(Y, 0.4)}`, "&:hover": { bgcolor: Y_DARK },
                  "&.Mui-disabled": { bgcolor: alpha(Y, 0.3), color: alpha(Y_INK, 0.4) } }}>
                {converting ? "Converting…" : `⚡ Convert ${promised.filter(p => p.decision !== "wait").length} to instant`}
              </Button>
            </Box>
          )}
        </>
      ) : (
        <Box sx={{ flex: 1, overflowY: "auto" }}>
          <Box sx={{ maxWidth: "100%", px: { md: 8, xl: 12 }, py: 4,
            display: "grid", gridTemplateColumns: { md: "400px 1px 1fr" },
            gap: "0px", alignItems: "start" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pr: 6 }}>
              <SummarySection />
            </Box>
            <Box sx={{ width: "1px", bgcolor: "divider", alignSelf: "stretch", minHeight: 600 }} />
            <Box sx={{ pl: 6 }}>
              <PayoutsSection />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default BlitzSplitManageDialog;